import {
  Dispatch,
  FC,
  SetStateAction,
  Suspense,
  useMemo,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EventData, Swipeable } from 'react-swipeable';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { EMatchStreamingViewModes } from 'interfaces/match-streaming';
import { GameInfo, GameOnly } from 'interfaces/sportsbook-data-levels';
import { MatchInfoCardSkeleton } from './skeleton';
import { useMatchInfoHide } from 'hooks/sportsbook/useMatchInfoHide';
import { useSportImage } from 'hooks/sportsbook/useSportImage';
import { useGetAvailableModes } from 'newhooks/sportsbook/matchStreaming/useGetAvailableModes';
import { setMatchStreamingViewMode } from 'store/actions/match-streaming';
import {
  getMatchStreamingGameData,
  getMatchStreamingType
} from 'store/selectors/match-streaming';
import './index.less';

const { MatchCardDefault } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-layout-default" */ './__partials/MatchCardLayouts/MatchCardDefault'
    )
);

const { MatchCardPistolArchery } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-layout-pistol-archery" */ './__partials/MatchCardLayouts/MatchCardPistolArchery'
    )
);

const { MatchCardPistolH2H } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-layout-pistol-h2h" */ './__partials/MatchCardLayouts/MatchCardPistolH2H'
    )
);

const { MatchCardArcheryH2HCompoundArchery } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-layout-archery-h2h-compound-archery" */ './__partials/MatchCardLayouts/MatchCardArcheryH2HCompoundArchery'
    )
);

const { MatchCardBasketballShots } = lazy(
  () =>
    import(
      /* webpackChunkName: "match-card-layout-basketball-shots" */ './__partials/MatchCardLayouts/MatchCardBasketballShots'
    )
);

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

export type TMatchInfoHideGame = Pick<
  GameOnly,
  | 'text_info'
  | 'info'
  | 'add_info_name'
  | 'tv_info'
  | 'start_ts'
  | 'team1_name'
  | 'team2_name'
  | 'stats'
  | 'team1_id'
  | 'team2_id'
  | 'id'
  | 'is_stat_available'
  | 'type'
  | 'markets_count'
  | 'is_started'
  | 'match_length'
  | 'is_blocked'
> & {
  info?: GameInfo;
  live_events?: Array<{
    event_type: any;
    team: 'team1' | 'team2';
    add_info: null | string;
    event_value?: number;
  }>;
};

export type TMatchInfoCardProps = {
  sportAlias: string;
  regionAlias?: string;
  competitionName?: string;
  loading?: boolean;
  regionName?: string;
  game?: TMatchInfoHideGame;
  setOpenTimeLine?: (arg: (prev: boolean) => boolean) => void;
  openTimeLine?: boolean;
};

const LAYOUTS: Record<
  string,
  FC<
    TMatchInfoCardProps & {
      sportImageSrc: string;
      setSelectedSet: Dispatch<SetStateAction<number>>;
      selectedSet: number;
    }
  >
> = {
  default: MatchCardDefault,
  PistolShooting: MatchCardPistolArchery,
  PistolHead2Head: MatchCardPistolH2H,
  ArcheryH2H: MatchCardArcheryH2HCompoundArchery,
  CompoundArchery: MatchCardArcheryH2HCompoundArchery,
  Archery: MatchCardPistolArchery,
  BasketballShots: MatchCardBasketballShots
};

export const MatchInfoCard: FC<TMatchInfoCardProps> = ({
  sportAlias,
  regionAlias,
  competitionName,
  loading,
  game: gameObj,
  regionName,
  openTimeLine,
  setOpenTimeLine
}) => {
  const dispatch = useDispatch();

  const history = useHistory();
  const matchStreamingGameData = useSelector(getMatchStreamingGameData);
  const matchStreamingType = useSelector(getMatchStreamingType);
  const sportImageSrc = useSportImage(sportAlias);
  const game = useMatchInfoHide({ game: gameObj });
  const availableModes = useGetAvailableModes();

  const [selectedSet, setSelectedSet] = useState(0);

  const MatchCard = useMemo(() => {
    return LAYOUTS[sportAlias] || LAYOUTS.default;
  }, [sportAlias]);

  const onSwiped = (data: EventData) => {
    if (data.dir === 'Down' && availableModes.length) {
      dispatch(setMatchStreamingViewMode(EMatchStreamingViewModes.minimized));

      history.push(
        `${mountPoint(window.location.pathname)}/${
          matchStreamingGameData?.sport
        }`
      );
    }
  };

  return (
    <Swipeable
      {...(isMobile() &&
        matchStreamingType && {
          onSwiped: onSwiped,
          preventDefaultTouchmoveEvent: false
        })}
      className={isMobile() ? 'matchInfo__wrapper-mobile' : ''}
    >
      <Suspense fallback={<MatchInfoCardSkeleton sportAlias={sportAlias} />}>
        <MatchCard
          sportAlias={sportAlias}
          regionAlias={regionAlias}
          competitionName={competitionName}
          game={game}
          loading={loading || !sportAlias || !game || !Object.keys(game).length}
          regionName={regionName}
          openTimeLine={openTimeLine}
          setOpenTimeLine={setOpenTimeLine}
          sportImageSrc={sportImageSrc}
          setSelectedSet={setSelectedSet}
          selectedSet={selectedSet}
        />
      </Suspense>
    </Swipeable>
  );
};
